import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { tablePadding, layoutUtils } from '@/lib/layout-utils';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { 
  MoreHorizontal, 
  Mail, 
  Edit, 
  UserCheck, 
  AlertCircle, 
  Plus,
  Users,
  User
} from 'lucide-react';
import { Family, Parent, Student } from '@/types/models';

interface FamilyTableRowProps {
  family: Family;
  parents: Parent[];
  students: Student[];
  onClick: () => void;
  onSendInvite: (parentId: string) => void;
  onEditParent: (parent: Parent) => void;
  onAddParent?: (familyId: string) => void;
  onAddStudent?: (familyId: string) => void;
}

const FamilyTableRow: React.FC<FamilyTableRowProps> = ({
  family,
  parents,
  students,
  onClick,
  onSendInvite,
  onEditParent,
  onAddParent,
  onAddStudent
}) => {
  const getStatusInfo = () => {
    // Check if family is inactive first
    if (family.is_active === false) {
      return {
        label: 'Inactive',
        variant: 'secondary' as const,
        icon: AlertCircle,
        color: 'text-gray-600'
      };
    }
    
    const hasParents = parents.length > 0;
    const hasStudents = students.length > 0;
    
    if (hasParents && hasStudents) {
      return {
        label: 'Complete',
        variant: 'default' as const,
        icon: UserCheck,
        color: 'text-green-600'
      };
    } else if (!hasParents && !hasStudents) {
      return {
        label: 'Setup Required',
        variant: 'destructive' as const,
        icon: AlertCircle,
        color: 'text-red-600'
      };
    } else {
      return {
        label: 'Incomplete',
        variant: 'secondary' as const,
        icon: AlertCircle,
        color: 'text-amber-600'
      };
    }
  };

  const status = getStatusInfo();
  const StatusIcon = status.icon;

  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || '?';
  };

  const handleRowClick = (e: React.MouseEvent) => {
    // Don't trigger row click if clicking on interactive elements
    if ((e.target as HTMLElement).closest('button, [role="button"]')) {
      return;
    }
    onClick();
  };

  return (
    <>
      {/* Family Name */}
      <td className={layoutUtils.getTableRowClasses(true, `${tablePadding.cell} ${family.is_active === false ? 'opacity-60' : ''}`)} onClick={handleRowClick}>
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 bg-gradient-to-br from-pink-100 to-purple-100 rounded-full flex items-center justify-center shrink-0 ${
            family.is_active === false ? 'bg-gray-100' : ''
          }`}>
            <span className={`text-xs font-medium ${
              family.is_active === false ? 'text-gray-500' : 'text-pink-600'
            }`}>
              {family.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="min-w-0">
            <div className={`font-medium truncate hover:text-primary transition-colors ${
              family.is_active === false ? 'text-gray-500' : ''
            }`}>
              {family.name}
            </div>
            <div className="text-sm text-muted-foreground">
              {parents.length + students.length} members
            </div>
          </div>
        </div>
      </td>

      {/* Parents */}
      <td className={layoutUtils.getTableRowClasses(true, tablePadding.cell)} onClick={handleRowClick}>
        <div className="min-w-0">
          {parents.length > 0 ? (
            <div className="space-y-1">
              {parents.slice(0, 2).map((parent) => (
                <div key={parent.id} className="flex items-center gap-2">
                  <Avatar className="h-5 w-5">
                    <AvatarFallback className="text-xs bg-blue-100 text-blue-700">
                      {getInitials(parent.first_name, parent.last_name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm truncate">
                    {parent.first_name} {parent.last_name}
                  </span>
                </div>
              ))}
              {parents.length > 2 && (
                <div className="text-xs text-muted-foreground ml-7">
                  +{parents.length - 2} more
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <User className="h-4 w-4" />
              <span className="text-sm">No parents</span>
            </div>
          )}
        </div>
      </td>

      {/* Students */}
      <td className={layoutUtils.getTableRowClasses(true, tablePadding.cell)} onClick={handleRowClick}>
        <div className="min-w-0">
          {students.length > 0 ? (
            <div className="space-y-1">
              {students.slice(0, 2).map((student) => (
                <div key={student.id} className="flex items-center gap-2">
                  <Avatar className="h-5 w-5">
                    <AvatarFallback className="text-xs bg-purple-100 text-purple-700">
                      {getInitials(student.first_name, student.last_name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm truncate">
                    {student.first_name} {student.last_name}
                  </span>
                </div>
              ))}
              {students.length > 2 && (
                <div className="text-xs text-muted-foreground ml-7">
                  +{students.length - 2} more
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Users className="h-4 w-4" />
              <span className="text-sm">No students</span>
            </div>
          )}
        </div>
      </td>

      {/* Status */}
      <td className={layoutUtils.getTableRowClasses(true, tablePadding.cell)} onClick={handleRowClick}>
        <Badge variant={status.variant} className="text-xs">
          <StatusIcon className="h-3 w-3 mr-1" />
          {status.label}
        </Badge>
      </td>

      {/* Actions */}
      <td className={layoutUtils.getTableRowClasses(false, tablePadding.cell)}>
        <div className="flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={onClick}>
                <Edit className="h-4 w-4 mr-2" />
                View Details
              </DropdownMenuItem>
              
              {parents.length > 0 && (
                <>
                  {parents.map((parent) => (
                    <DropdownMenuItem 
                      key={parent.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSendInvite(parent.id);
                      }}
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Invite {parent.first_name}
                    </DropdownMenuItem>
                  ))}
                </>
              )}
              
              {parents.length === 0 && onAddParent && (
                <DropdownMenuItem onClick={() => onAddParent(family.id)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Parent
                </DropdownMenuItem>
              )}
              
              {students.length === 0 && onAddStudent && (
                <DropdownMenuItem onClick={() => onAddStudent(family.id)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Student
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </>
  );
};

export default FamilyTableRow;